import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FadeUp, SectionTag } from "../components/site/Reveal";

const PRINCIPLES = [
  { code: "P-01", title: "Ownership over dependency", desc: "We don't run your back office. We teach you to build one you'd bet the business on — because you built every piece of it." },
  { code: "P-02", title: "Simple beats sophisticated", desc: "A spreadsheet you actually use beats a platform you pay for and ignore. We start with the simplest tool that works." },
  { code: "P-03", title: "Built for hands, not desks", desc: "Every workflow assumes you're on a job site, in a truck, or between customers. Mobile-first, minutes-a-day maintenance." },
  { code: "P-04", title: "Systems before staff", desc: "Your first hire shouldn't inherit chaos. Build the operating system first, then hand someone the keys." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-navy pt-16">
      <section className="blueprint-grid grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <FadeUp className="lg:col-span-7">
            <SectionTag dark>About // The Operator's Creed</SectionTag>
            <h1 className="font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.03] mt-6">
              We build for the ones who <span className="text-safety">do the work.</span>
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed mt-6 max-w-2xl">
              WorkBase Systems exists for a specific person: the owner-operator of a
              hands-on service business who already has paying customers — and is
              personally holding together the leads, quotes, calendar, invoices, and
              follow-up with memory and late nights.
            </p>
            <p className="text-slate-400 leading-relaxed mt-4 max-w-2xl">
              Most business software is designed for desk workers and sold to everyone.
              Most coaching is theory. The Back Office Blueprint is neither: it's a
              guided build of the seven systems every service business runs on, using
              tools simple enough to keep after we leave.
            </p>
          </FadeUp>
          <FadeUp delay={0.2} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full border border-safety/40" />
              <div className="relative bg-charcoal border border-white/10 p-8 grain">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-safety mb-6">[WHAT "SYSTEMS" MEANS HERE]</p>
                <div className="space-y-4">
                  {[
                    ["Lead comes in", "logged + answered in seconds"],
                    ["Quote goes out", "same day, priced from real costs"],
                    ["Job gets done", "calendar confirmed the details"],
                    ["Invoice lands", "before the truck leaves"],
                    ["Payment clears", "no chase, no awkward texts"],
                    ["Follow-up fires", "review, rebook, referral"],
                  ].map(([a, b], i) => (
                    <div key={a} className="flex items-center gap-4">
                      <span className="font-mono text-safety text-xs w-7 shrink-0">0{i + 1}</span>
                      <div className="flex-1 flex justify-between gap-3 border-b border-white/10 pb-3">
                        <span className="text-white text-sm font-medium">{a}</span>
                        <span className="text-slate-500 text-xs text-right">{b}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-7">
                  The machine around the work — that's what we build together
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="bg-bone text-navy blueprint-grid-light">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <FadeUp className="mb-14">
            <SectionTag>Operating Principles // How We Work</SectionTag>
            <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl mt-5">
              Four rules we won't bend.
            </h2>
          </FadeUp>
          <div className="grid sm:grid-cols-2 gap-px bg-slate-300 border border-slate-300">
            {PRINCIPLES.map((p, i) => (
              <FadeUp key={p.code} delay={i * 0.08} className="h-full">
                <div className="bg-bone p-8 h-full" data-testid={`principle-${p.code.toLowerCase()}`}>
                  <p className="font-mono text-[11px] tracking-[0.2em] text-safety">[{p.code}]</p>
                  <h3 className="font-display font-bold text-xl mt-3">{p.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mt-3">{p.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp className="mt-16 text-center">
            <Link to="/apply" data-testid="about-apply-btn"
              className="inline-flex items-center gap-2 bg-safety hover:bg-safety-hover text-white font-semibold px-10 py-4 transition-colors">
              Apply for the Blueprint <ArrowRight size={17} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
